import parse from 'html-react-parser';
import _ from 'lodash';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
// import EmptyArticle from 'src/components/empty';
// import Layout from 'src/components/layouts';
import { GetServerSideProps, NextPage } from 'next';
import isEmpty from 'lodash/isEmpty';
import { TInitPropsMember } from 'src/services/member-auth/member.auth.service';
import { APP_URL, IS_NO_ROBOT_INDEX, WEB_NAME } from 'src/constants';
import { APP_IMG } from 'src/constants';
import { GET } from 'src/services';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { escapeChar } from 'src/utils/helper';
import useUser from 'src/utils/useUser';
import useResponsive from 'src/utils/devices';

const GalleryPreview = dynamic(
  () => import('src/components/gallery/gallery-preview')
);
const Layout = dynamic(import('src/components/layout'));
const PageError = dynamic(import('pages/404'));

interface ArticleData {
  id: string;
  title: string;
  data: Array<{ id: string }>;
}

interface MetaData {
  keyword: string;
}

interface DataAdsHeader {
  header_mobile?: string;
  header_desktop?: string;
}

type Props = TInitPropsMember & {
  article: ArticleData; // ทำให้ article required
  meta?: MetaData;
  id?: string;
  dataAdsHeader?: DataAdsHeader;
  lastNews: any[]; // ทำให้ required
  ads: any; // ทำให้ required
};


const PageGalleriesPreview: NextPage<Props> = props => {
  const _data = useUser();
  const router = useRouter();
  const { isMobile } = useResponsive();
  const _index = props?.article?.data?.findIndex((item) => item.id === props?.id) ?? -1;

  const _length = props?.article?.data?.length ?? 0;

  const sessionSevId = _data?.user ? decryptionClient(_data?.user) : null;
  const { meta, dataAdsHeader } = props;
  if (isEmpty(props)) return <PageError sessionSevId={sessionSevId} />;
  const heading =
    `${props?.article?.title ?? 'ไม่พบข้อมูล'} รูปภาพที่ ${_index >= 0 ? _index + 1 : 0}/${_length}`;
  const articleTitleSeo = escapeChar(`รวมอัลบั้มรูป : ${heading ?? "ไม่พบข้อมูล"}`);
  const articleDescriptionSeo = escapeChar(`รวมอัลบั้มรูป : ${props?.article?.title ?? ""}`);
  const baseUrlCategory = `${APP_URL}${router.asPath}`;

  return (
    <>
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={`รวมอัลบั้มรูป : ${heading ?? "ไม่พบข้อมูล"}`}
        description={`รวมอัลบั้มรูป : ${props?.article?.title ?? ""}`}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          site_name: WEB_NAME,
          title: `รวมอัลบั้มรูป : ${heading ?? "ไม่พบข้อมูล"}`,
          description: `รวมอัลบั้มรูป : ${props?.article?.title ?? ""}`,
          url: baseUrlCategory,
          images: [
            {
              url: APP_IMG,
              width: 1920,
              height: 1080,
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: meta?.keyword ?? ''
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={baseUrlCategory} />
        <meta property='twitter:title' content={`รวมอัลบั้มรูป : ${heading ?? "ไม่พบข้อมูล"}`} />
        <meta property='twitter:description' content={`รวมอัลบั้มรูป : ${props?.article?.title ?? ""}`} />
        <meta property='twitter:image' content={APP_IMG} />

        {/* --------------------------------- Truehit -------------------------------- */}
        <script type='text/javascript' dangerouslySetInnerHTML={{
          __html: `
            __th_page="galleries";
          `
        }} />

        {/* ---------------------------- SNIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "${WEB_NAME}",
              "url": "${APP_URL.WEB}",
              "description": "${articleDescriptionSeo}",
              "sameAs": [
                "${APP_URL.FACEBOOK}",
                "${APP_URL.TWITTER}",
                "${APP_URL.YOUTUBE}",
                "${APP_URL.LINE}"
              ],
              "potentialAction": {
                "@type": "SearchAction",
                "target": "${APP_URL.WEB}search?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          `
          }}
        />

        {/* ---------------------------- SNIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
              {
                "@context": "http://schema.org",
                "@type": "WebPage",
                "name": "${articleTitleSeo}",
                "description": "${articleDescriptionSeo}",
                "publisher": {
                  "@type": "ProfilePage",
                  "name": "${WEB_NAME}"
                }
              }
            `
          }}
        />

        {/* ------------------------ SNIPPET : BreadcrumbList ------------------------ */}
        <script type='application/ld+json' dangerouslySetInnerHTML={{
          __html: `
              {
                "@context": "http://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "หน้าแรก",
                    "item": "${APP_URL}"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "${articleTitleSeo}",
                    "item": "${baseUrlCategory}"
                  }
                ]
              }
            `
        }} />
        {!isMobile && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {isMobile && !_.isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}

      </Head>
      <Layout sessionSevId={sessionSevId}>
        <H1 className='head-seo'>{`รวมอัลบั้มรูป : ${heading ?? 'ไม่พบข้อมูล'}`}</H1>

        <GalleryPreview {...props} />
      </Layout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ res, query }: any) => {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    article: {},
    related: [],
    lastNews: [],
    solrRelated: [],
    affiliate: [],
    adsNative: null,
    ads: [],
    sessionSevId: null,
    id: null,
    group: null,
    meta: null,
    dataAdsHeader: null // ป้องกัน undefined
  };
  try {
    const id = query.group || query.id;
    const resData = await GET(`/api/v1.0/gallery/contents/${id}`, false, true);
    const adsHeaders = await GET('/api/headerAds');
    if (!_.isEmpty(adsHeaders)) {
      initProps.dataAdsHeader = adsHeaders;
    }
    if (!_.isEmpty(resData?.data)) {
      initProps.article = (resData?.data);
      initProps.meta = (resData?.meta);

      initProps.related = resData?.related ? resData?.related.slice(0, 5) : [];
      initProps.lastNews = resData?.last_news ? resData?.last_news.slice(0, 5) : [];
      initProps.solrRelated = resData?.solrRelated ? resData?.solrRelated.slice(0, 5) : [];
      initProps.affiliate = resData?.affiliate || [];
      initProps.ads = resData?.ads || [];
      initProps.adsNative = resData?.adsNative || null;
      initProps.id = query?.id;
      initProps.group = query?.group;
    }
  } catch (err: any) {
    console.log('==========> LOTTERY ERROR', err?.message);
    initProps.errMessage = err?.message;
    initProps.statusCode = err?.response?.status || 500;

    res.statusCode = initProps.statusCode;
    res.end();
  }

  return {
    props: initProps
  };
};

export default PageGalleriesPreview;
const H1 = styled.h1`
    position: absolute;
    visibility: hidden;
    overflow: hidden;
`;